import type { DrugTest } from "@repo/models";

import { styles } from "@repo/helpers";
import { Button, ButtonTypes, MoveableDialog } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { DrugTestsFilter } from "../../api/drugTestsApi";

import DrugTestsApi, { controller } from "../../api/drugTestsApi";
import { QUERY_KEYS } from "../../api/queries";
import { AgentPortal } from "../../apps/agentPortal";
import { TLPGrid } from "../../components/tlpGrid";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import { useGridColumns } from "../../hooks/useGridColumns";
import { BulkImportModal } from "./components/BulkImportModal";
import style from "./style.module.scss";

interface Props {
	filters: DrugTestsFilter;
	setFilters: (filters: DrugTestsFilter) => void;
}

export function DrugTestsGrid({ filters, setFilters }: Props) {
	const [bulkImportOpen, setBulkImportOpen] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedDt, setSelectedDt] = useState<DrugTest | undefined>(undefined);

	const drugTestsQuery = useQuery({
		queryKey: [QUERY_KEYS.DrugTests.Grid, filters],
		queryFn: ({ signal }) => DrugTestsApi.getDrugTests(filters, { signal }),
	});

	const openPortalModal = (dt: DrugTest) => {
		setSelectedDt(dt);
		setDialogOpen(true);
	};

	const { columns, isLoading: isLoadingColumns } = useGridColumns("DrugTest", DrugTestsApi.getGridColumns, {
		openPortalModal,
	});

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<DrugTest, DrugTestsFilter>
				id="drugTestsGrid"
				title="Drug Tests"
				gridNameForExport="DrugTests"
				keyProperty="drugTestId"
				filters={filters}
				setFilters={setFilters}
				query={drugTestsQuery}
				apiRoute={controller}
				gridApi={DrugTestsApi}
				columns={columns}
				exportFunc={DrugTestsApi.getDrugTests}
				additionalActions={
					<Button types={[ButtonTypes.Green]} onClick={() => setBulkImportOpen(true)}>
						Bulk Upload
					</Button>
				}
				selected={selectedDt?.drugTestId ? [selectedDt.drugTestId] : []}
			/>

			{selectedDt && (
				<MoveableDialog
					open={dialogOpen}
					onOpenChange={() => setDialogOpen(undefined)}
					title="Rep Portal"
					initialSize={getInitialDialogSize()}
					resizable
					movable
					className={styles("agent-portal-modal", style.noPadding)}
					contentClassName={style.noPadding}
				>
					<AgentPortal
						key={selectedDt?.drugTestId ?? 0}
						repId={selectedDt?.representativeId ?? null}
						selectedTab="DrugTests"
						entityId={selectedDt?.drugTestId}
					/>
				</MoveableDialog>
			)}

			<BulkImportModal
				visible={bulkImportOpen}
				onClose={() => setBulkImportOpen(false)}
				onSuccess={() => drugTestsQuery.refetch()}
			/>
		</>
	);
}
